import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { RefreshCw, Brain, Gauge, Scale, Activity, Info } from 'lucide-react';
import { apiGet, formatTs } from './ops_helpers';

const REGIME_META = {
  trend_up: { label: 'Tendencia ↑', cls: 'bg-emerald-600' },
  trend_down: { label: 'Tendencia ↓', cls: 'bg-rose-600' },
  range: { label: 'Lateral', cls: 'bg-sky-600' },
  high_vol: { label: 'Alta volatilidad', cls: 'bg-amber-600' },
  low_vol: { label: 'Baja volatilidad', cls: 'bg-indigo-600' },
  unknown: { label: 'Desconocido', cls: 'bg-zinc-700' },
  all: { label: 'Todos (global)', cls: 'bg-zinc-600' },
};

const FACTOR_LABELS = {
  funding: 'Funding', long_short: 'Long/Short', oi_price: 'OI vs Precio',
  rsi: 'RSI', liquidations: 'Liquidaciones', taker: 'Taker flow',
};
const WEIGHT_ENV = {
  funding: 'SENT_W_FUNDING', long_short: 'SENT_W_LS', oi_price: 'SENT_W_OI_PRICE',
  rsi: 'SENT_W_RSI', liquidations: 'SENT_W_LIQ', taker: 'SENT_W_TAKER',
};
const FACTOR_ORDER = ['funding', 'long_short', 'oi_price', 'rsi', 'liquidations', 'taker'];

const regimeBadge = (r) => {
  const m = REGIME_META[r] || REGIME_META.unknown;
  return <Badge data-testid={`sent-regime-${r}`} className={`${m.cls} text-white`}>{m.label}</Badge>;
};

// Badge SOLO VISUAL de validación de edge por régimen. No dispara ninguna acción.
const edgeStatus = (m) => {
  if (!m) return null;
  const ev = m.evaluated || 0;
  const wr = m.win_rate_pct;
  const exp = m.expectancy_pct;
  const wrOk = wr != null && wr > 55;
  const expOk = exp == null || exp > 0; // expectancy positiva si está disponible
  if (ev >= 50 && wrOk && expOk) return { label: 'Edge validado — revisar pesos', cls: 'bg-emerald-600', strong: true };
  if (ev >= 20 && wrOk && expOk) return { label: 'Edge suficiente para revisar', cls: 'bg-amber-600', strong: false };
  return null;
};

const fmt = (v, d = 2, suffix = '') =>
  (v === null || v === undefined) ? '—' : `${Number(v).toFixed(d)}${suffix}`;

const signalCls = (s) =>
  s === 'LONG' ? 'text-emerald-400' : s === 'SHORT' ? 'text-rose-400' : 'text-zinc-400';

const Metric = ({ label, value, testId }) => (
  <div className="flex flex-col" data-testid={testId}>
    <span className="text-xs text-zinc-500">{label}</span>
    <span className="text-sm font-semibold text-zinc-100">{value}</span>
  </div>
);

const GATE_LABELS = {
  dual_threshold: 'Doble umbral', regime_gate: 'Puerta de régimen',
  trend_filter: 'Filtro tendencia', atr_filter: 'Filtro ATR',
};

const GateChip = ({ name, gate }) => (
  <div
    data-testid={`sent-shadow-gate-${name}`}
    className={`rounded-md border px-2 py-1.5 text-xs ${gate?.passed
      ? 'border-emerald-800/60 bg-emerald-950/40 text-emerald-300'
      : 'border-rose-800/60 bg-rose-950/40 text-rose-300'}`}
    title={gate?.note || ''}
  >
    <span className="font-semibold">{gate?.passed ? '✓' : '✕'} {GATE_LABELS[name] || name}</span>
    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{gate?.note || '—'}</p>
  </div>
);

const decisionCls = (d) =>
  d === 'TRADE_NORMAL' ? 'text-emerald-400' : d === 'TRADE_SMALL' ? 'text-amber-400' : 'text-zinc-400';

const SCAN_ORDER = ['BTC', 'ETH', 'SOL', 'DOGE', 'XRP'];

// Escáner multisímbolo en SOMBRA: mismo motor + puertas para ETH/SOL/DOGE/XRP,
// elige la mejor señal operable. Solo visualización.
const ScannerCard = ({ scanner }) => {
  if (!scanner) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-scanner-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-400" /> Escáner Multisímbolo (sombra)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-500 text-center py-3" data-testid="sent-scanner-empty">
            Aún sin datos del escáner. Despliega la versión con escáner en el VPS del motor.
          </p>
        </CardContent>
      </Card>
    );
  }
  const symbols = scanner.symbols || {};
  const best = scanner.best;
  const stats = scanner.stats || {};
  const weekly = scanner.weekly_ranking || [];
  const keys = SCAN_ORDER.filter((k) => symbols[k]).concat(Object.keys(symbols).filter((k) => !SCAN_ORDER.includes(k)));
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-scanner-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-400" /> Escáner Multisímbolo (sombra)</CardTitle>
          <Badge className="bg-violet-700 text-white">MODO SOMBRA · no opera</Badge>
        </div>
        <CardDescription className="text-xs">
          Mismo motor + puertas para BTC/ETH/SOL/DOGE/XRP cada {Math.round((scanner.interval_sec || 300) / 60)} min. Elige la mejor señal operable en sombra.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {best ? (
          <div className="flex items-center gap-3 rounded-md border border-emerald-800/60 bg-emerald-950/40 px-3 py-2" data-testid="sent-scanner-best">
            <Badge className="bg-emerald-600 text-white">MEJOR</Badge>
            <span className="font-bold text-zinc-100">{best.symbol}</span>
            <span className={`font-semibold ${signalCls(best.signal)}`}>{best.signal}</span>
            <span className="text-xs text-zinc-400">conf {fmt(best.confidence, 0)} · score {fmt(best.score, 1)} · {best.decision} · riesgo {fmt(best.risk_pct, 3, '%')}</span>
          </div>
        ) : (
          <p className="text-xs text-zinc-500" data-testid="sent-scanner-nobest">Ninguna señal pasa las puertas ahora mismo (correcto en mercado débil/lateral).</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sent-scanner-table">
            <thead>
              <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                <th className="text-left py-2 pr-3">Símbolo</th>
                <th className="text-left px-2">Señal viva</th>
                <th className="text-right px-2">Score</th>
                <th className="text-right px-2">Conf</th>
                <th className="text-left px-2">Régimen</th>
                <th className="text-left px-2">Decisión sombra</th>
                <th className="text-left px-2">Bloqueada por</th>
                <th className="text-right pl-2">Ev. (apr/bloq)</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const s = symbols[k] || {};
                const st = stats[k] || {};
                const isBest = best && best.symbol === k;
                return (
                  <tr key={k} className={`border-b border-zinc-800/50 ${isBest ? 'bg-emerald-950/20' : ''}`} data-testid={`sent-scanner-row-${k}`}>
                    <td className="py-2 pr-3 font-semibold text-zinc-100">{k}{isBest && <span className="text-emerald-400 ml-1">★</span>}</td>
                    <td className={`px-2 font-semibold ${signalCls(s.signal)}`}>{s.error ? 'ERROR' : (s.signal || '—')}</td>
                    <td className="text-right px-2 text-zinc-300">{fmt(s.score, 1)}</td>
                    <td className="text-right px-2 text-zinc-300">{fmt(s.confidence, 0)}</td>
                    <td className="px-2">{regimeBadge(s.regime || 'unknown')}</td>
                    <td className={`px-2 font-semibold ${decisionCls(s.shadow_decision)}`}>{s.shadow_decision || '—'}</td>
                    <td className="px-2 text-xs text-zinc-500">{(s.blocked_by || []).map((g) => GATE_LABELS[g] || g).join(', ') || '—'}</td>
                    <td className="text-right pl-2 text-xs text-zinc-500">{st.events ?? 0} ({st.approved ?? 0}/{st.blocked ?? 0})</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {weekly.length > 0 && (
          <div data-testid="sent-scanner-weekly">
            <p className="text-xs font-semibold text-zinc-300 mb-2">Ranking semanal (edge sombra) — dónde ampliar</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {weekly.slice(0, 2).map((w) => (
                <div key={w.week} className="rounded-md border border-zinc-800 p-2.5" data-testid={`sent-scanner-week-${w.week}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-zinc-200">{w.week}</span>
                    <span className="text-[11px] text-zinc-600">por expectancy</span>
                  </div>
                  {(w.ranking || []).length === 0 ? (
                    <p className="text-xs text-zinc-600">sin señales evaluadas</p>
                  ) : (
                    (w.ranking || []).map((s, i) => (
                      <div key={s.symbol} className="flex items-center justify-between text-xs py-0.5" data-testid={`sent-scanner-rank-${w.week}-${s.symbol}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-6 text-center rounded font-semibold ${i === 0 ? 'bg-emerald-700 text-white' : 'bg-zinc-800 text-zinc-400'}`}>{i + 1}º</span>
                          <span className="font-semibold text-zinc-100">{s.symbol}</span>
                        </span>
                        <span className="text-zinc-400">
                          <span className={(s.expectancy_pct || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{fmt(s.expectancy_pct, 3, '%')}</span>
                          {' '}· WR {s.win_rate_pct != null ? fmt(s.win_rate_pct, 0, '%') : '—'} · n={s.evaluated}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Calibrador de umbrales: grid (score, conf) ± regime gate sobre el histórico evaluado.
const CalibratorCard = ({ calibration }) => {
  if (!calibration) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-calib-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Scale className="w-4 h-4 text-amber-400" /> Calibrador de umbrales (sombra)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-500 text-center py-3" data-testid="sent-calib-empty">Aún sin datos del calibrador.</p>
        </CardContent>
      </Card>
    );
  }
  const rec = calibration.recommended;
  const cur = calibration.current || {};
  const combos = calibration.top_combos || [];
  const insufficient = calibration.status !== 'ok';
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-calib-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2"><Scale className="w-4 h-4 text-amber-400" /> Calibrador de umbrales (sombra)</CardTitle>
          {insufficient
            ? <Badge className="bg-zinc-700 text-white" data-testid="sent-calib-status">acumulando datos ({calibration.samples_evaluated ?? 0}/{calibration.min_samples ?? 30})</Badge>
            : <Badge className="bg-emerald-700 text-white" data-testid="sent-calib-status">propuesta disponible</Badge>}
        </div>
        <CardDescription className="text-xs">
          Re-aplica combos de score/conf mínimos (± puerta de régimen) sobre las {calibration.samples_evaluated ?? 0} señales evaluadas del histórico.
          No cambia nada: la adopción es manual vía SENT_SHADOW_*.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-zinc-800 px-3 py-2" data-testid="sent-calib-current">
            <span className="text-xs text-zinc-500 block">Umbrales sombra actuales</span>
            <span className="text-sm font-semibold text-zinc-100">score ≥ {fmt(cur.score_min, 0)} · conf ≥ {fmt(cur.conf_min, 0)} · gate régimen ON</span>
          </div>
          <div className={`rounded-md border px-3 py-2 ${rec ? 'border-emerald-800/60 bg-emerald-950/30' : 'border-zinc-800'}`} data-testid="sent-calib-recommended">
            <span className="text-xs text-zinc-500 block">Recomendado (mejor expectancy con muestra)</span>
            {rec
              ? <span className="text-sm font-semibold text-emerald-300">score ≥ {fmt(rec.score_min, 0)} · conf ≥ {fmt(rec.conf_min, 0)} · gate {rec.regime_gate ? 'ON' : 'OFF'} → exp {fmt(rec.expectancy_pct, 3, '%')} (n={rec.evaluated})</span>
              : <span className="text-sm text-zinc-500">— aún sin muestra suficiente</span>}
          </div>
        </div>
        {combos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sent-calib-table">
              <thead>
                <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-right py-2 px-2">Score ≥</th>
                  <th className="text-right px-2">Conf ≥</th>
                  <th className="text-left px-2">Gate régimen</th>
                  <th className="text-right px-2">N</th>
                  <th className="text-right px-2">Win-rate</th>
                  <th className="text-right px-2">PF</th>
                  <th className="text-right pl-2">Expectancy</th>
                </tr>
              </thead>
              <tbody>
                {combos.slice(0, 5).map((c, i) => (
                  <tr key={i} className="border-b border-zinc-800/50" data-testid={`sent-calib-row-${i}`}>
                    <td className="text-right py-1.5 px-2 text-zinc-300">{fmt(c.score_min, 0)}</td>
                    <td className="text-right px-2 text-zinc-300">{fmt(c.conf_min, 0)}</td>
                    <td className="px-2 text-zinc-400 text-xs">{c.regime_gate ? 'ON' : 'OFF'}</td>
                    <td className="text-right px-2 text-zinc-300">{c.evaluated ?? 0}</td>
                    <td className="text-right px-2 text-zinc-100 font-semibold">{c.win_rate_pct != null ? fmt(c.win_rate_pct, 1, '%') : '—'}</td>
                    <td className="text-right px-2 text-zinc-300">{fmt(c.profit_factor, 2)}</td>
                    <td className={`text-right pl-2 ${(c.expectancy_pct || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmt(c.expectancy_pct, 3, '%')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Card del Cerebro Sombra (Fase 1): qué HABRÍA hecho el brain con las puertas
// nuevas vs lo que hace la señal en vivo. Solo visualización.
const ShadowBrainCard = ({ shadow, liveSignal }) => {
  const cur = shadow?.current || null;
  const stats = shadow?.stats || {};
  const evalApproved = stats?.evaluated?.approved || {};
  const evalBlocked = stats?.evaluated?.blocked || {};
  const blockedBy = stats?.blocked_by || {};
  if (!cur && !stats.events_total) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Brain className="w-4 h-4 text-violet-400" /> Cerebro Sombra (Fase 1)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-500 text-center py-3" data-testid="sent-shadow-empty">
            Aún sin datos del Shadow Brain. Despliega la Fase 1 en el VPS del motor y espera al próximo reporte.
          </p>
        </CardContent>
      </Card>
    );
  }
  const bucketRow = (label, m, testId, cls) => (
    <tr className="border-b border-zinc-800/50" data-testid={testId}>
      <td className={`py-2 pr-3 font-semibold ${cls}`}>{label}</td>
      <td className="text-right px-2 text-zinc-300">{m.evaluated ?? 0}</td>
      <td className="text-right px-2 text-zinc-100 font-semibold">{m.win_rate_pct != null ? fmt(m.win_rate_pct, 1, '%') : '—'}</td>
      <td className="text-right px-2 text-zinc-300">{fmt(m.profit_factor, 2)}</td>
      <td className={`text-right px-2 ${(m.expectancy_pct || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmt(m.expectancy_pct, 3, '%')}</td>
    </tr>
  );
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-shadow-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Brain className="w-4 h-4 text-violet-400" /> Cerebro Sombra (Fase 1)
          </CardTitle>
          <Badge className="bg-violet-700 text-white" data-testid="sent-shadow-mode">MODO SOMBRA · no opera</Badge>
        </div>
        <CardDescription className="text-xs">
          Doble umbral + puerta de régimen + filtro de tendencia + filtro ATR. Registra qué habría
          hecho, para validar el edge antes de activar nada.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cur && (
          <>
            <div className="flex items-baseline gap-4 flex-wrap">
              <div>
                <span className="text-xs text-zinc-500 block">Señal en vivo</span>
                <span className={`text-lg font-bold ${signalCls(liveSignal)}`} data-testid="sent-shadow-live">{liveSignal || 'NO_TRADE'}</span>
              </div>
              <span className="text-zinc-600">→</span>
              <div>
                <span className="text-xs text-zinc-500 block">Decisión sombra</span>
                <span className={`text-lg font-bold ${decisionCls(cur.decision)}`} data-testid="sent-shadow-decision">{cur.decision || 'NO_TRADE'}</span>
              </div>
              <Metric label="Riesgo sombra" value={`${fmt(cur.risk_pct, 3, '%')} (x${fmt(cur.risk_mult, 2)})`} testId="sent-shadow-risk" />
              {cur.differs_from_live && (
                <Badge className="bg-amber-600 text-white" data-testid="sent-shadow-differs">la sombra lo habría FILTRADO</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {['dual_threshold', 'regime_gate', 'trend_filter', 'atr_filter'].map((g) => (
                <GateChip key={g} name={g} gate={cur.gates?.[g]} />
              ))}
            </div>
          </>
        )}
        <div>
          <div className="flex items-center gap-3 flex-wrap text-xs text-zinc-500 mb-2" data-testid="sent-shadow-counters">
            <span>Eventos: <b className="text-zinc-200">{stats.events_total ?? 0}</b></span>
            <span>Aprobadas: <b className="text-emerald-400">{stats.approved ?? 0}</b></span>
            <span>Bloqueadas: <b className="text-rose-400">{stats.blocked ?? 0}</b></span>
            {Object.entries(blockedBy).map(([g, n]) => (
              <span key={g} className="text-zinc-600">({GATE_LABELS[g] || g}: {n})</span>
            ))}
          </div>
          <table className="w-full text-sm" data-testid="sent-shadow-table">
            <thead>
              <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                <th className="text-left py-2 pr-3">Señales evaluadas</th>
                <th className="text-right px-2">N</th>
                <th className="text-right px-2">Win-rate</th>
                <th className="text-right px-2">PF</th>
                <th className="text-right px-2">Expectancy</th>
              </tr>
            </thead>
            <tbody>
              {bucketRow('Aprobadas por las puertas', evalApproved, 'sent-shadow-stats-approved', 'text-emerald-400')}
              {bucketRow('Bloqueadas por las puertas', evalBlocked, 'sent-shadow-stats-blocked', 'text-rose-400')}
            </tbody>
          </table>
          <p className="text-[11px] text-zinc-600 mt-2">
            Si las bloqueadas rinden peor que las aprobadas, las puertas añaden edge → candidatas a activarse.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export const SentimentInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/api/ops/sentiment/insights');
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(e.message || 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="text-zinc-500 p-8 text-center" data-testid="sent-loading">Cargando estado del Sentiment Engine…</div>;
  }

  if (!data || data.exists === false) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="sent-empty">
        <CardContent className="p-8 text-center text-zinc-400 space-y-3">
          <Brain className="w-8 h-8 mx-auto text-zinc-600" />
          <p>Aún sin datos del Sentiment Engine.</p>
          <p className="text-xs text-zinc-500">
            {data?.reason || 'El reporter del VPS todavía no ha enviado nada.'} Instala el reporter
            (timer cada 5 min) en el VPS y vuelve aquí.
          </p>
          <Button data-testid="sent-refresh-empty" variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" /> Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  const regime = data.regime || {};
  const sig = data.current_signal || {};
  const shadow = data.shadow || (sig.shadow ? { current: sig.shadow, stats: {} } : null);
  const learn = data.learn || {};
  const regimeMetrics = learn.regime_metrics || {};
  const proposals = learn.proposals || {};
  const currentW = learn.current_weights || {};
  const minSamples = learn.min_samples ?? 20;

  const regimeKeys = Object.keys(regimeMetrics).length
    ? Object.keys(regimeMetrics)
    : Object.keys(proposals);

  return (
    <div className="space-y-4" data-testid="sentiment-insights-panel">
      {/* Cabecera + estado de frescura */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-orange-400" />
          <h3 className="text-lg font-semibold text-zinc-100">Sentiment Engine · {data.symbol || 'BTC'}</h3>
          {data.stale
            ? <Badge className="bg-amber-600 text-white" data-testid="sent-stale">desactualizado ({Math.round((data.age_sec || 0) / 60)} min)</Badge>
            : <Badge className="bg-emerald-700 text-white" data-testid="sent-fresh">en vivo</Badge>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500" data-testid="sent-received-at">Actualizado: {formatTs(data.received_at)}</span>
          <Button data-testid="sent-refresh" variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {/* Banner solo-lectura */}
      <div className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2" data-testid="sent-readonly-banner">
        <Info className="w-4 h-4 text-sky-400 shrink-0" />
        Panel de <b className="mx-1 text-zinc-200">solo visualización</b>. No aplica pesos ni reinicia bots.
        La adopción de pesos es manual vía script en el VPS (con APPLY=1).
      </div>

      {/* Régimen actual + señal actual */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Gauge className="w-4 h-4 text-sky-400" /> Régimen actual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-3">
              {regimeBadge(regime.regime || 'unknown')}
              <span className="text-xs text-zinc-500">fuerza {fmt((regime.strength ?? 0) * 100, 0, '%')}</span>
            </div>
            <p className="text-xs text-zinc-400" data-testid="sent-regime-basis">{regime.basis || '—'}</p>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <Metric label="Momentum 1h" value={fmt(regime.momentum_1h_pct, 2, '%')} testId="sent-mom" />
              <Metric label="Δ OI 1h" value={fmt(regime.oi_change_1h_pct, 2, '%')} testId="sent-oi" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-orange-400" /> Señal actual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-baseline gap-3">
              <span className={`text-2xl font-bold ${signalCls(sig.signal)}`} data-testid="sent-signal">{sig.signal || 'NO_TRADE'}</span>
              <span className="text-xs text-zinc-500">conf {fmt(sig.confidence, 0)} · score {fmt(sig.score, 1)}</span>
            </div>
            <p className="text-xs text-zinc-400 line-clamp-2" data-testid="sent-reason">{sig.reason || '—'}</p>
            <div className="grid grid-cols-3 gap-2 pt-1">
              <Metric label="Acuerdo" value={fmt(sig.agreement, 2)} testId="sent-agreement" />
              <Metric label="Calidad datos" value={fmt((sig.data_quality ?? 0) * 100, 0, '%')} testId="sent-dq" />
              <Metric label="Precio" value={sig.price ? `$${Number(sig.price).toLocaleString('en-US')}` : '—'} testId="sent-price" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cerebro Sombra Fase 1 */}
      <ShadowBrainCard shadow={shadow} liveSignal={sig.signal} />

      {/* Escáner multisímbolo + Calibrador */}
      <ScannerCard scanner={data.scanner} />
      <CalibratorCard calibration={data.calibration} />

      {/* Métricas por régimen */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" /> Métricas por régimen</CardTitle>
          <CardDescription className="text-xs">Edge realizado de las señales evaluadas, separado por régimen de mercado.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {regimeKeys.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4" data-testid="sent-no-regime-metrics">Aún sin señales evaluadas. Acumulando datos…</p>
          ) : (
            <table className="w-full text-sm" data-testid="sent-regime-table">
              <thead>
                <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-left py-2 pr-3">Régimen</th>
                  <th className="text-right px-2">Señales</th>
                  <th className="text-right px-2">Evaluadas</th>
                  <th className="text-right px-2">Win-rate</th>
                  <th className="text-right px-2">PF</th>
                  <th className="text-right px-2">Expectancy</th>
                  <th className="text-right px-2">Conf. media</th>
                  <th className="text-right px-2">Score medio</th>
                  <th className="text-right pl-2">Estado del edge</th>
                </tr>
              </thead>
              <tbody>
                {regimeKeys.map((rk) => {
                  const m = regimeMetrics[rk] || {};
                  const edge = edgeStatus(m);
                  return (
                    <tr key={rk} className="border-b border-zinc-800/50" data-testid={`sent-regime-row-${rk}`}>
                      <td className="py-2 pr-3">{regimeBadge(rk)}</td>
                      <td className="text-right px-2 text-zinc-300">{m.signals_total ?? 0}</td>
                      <td className="text-right px-2 text-zinc-300">{m.evaluated ?? 0}</td>
                      <td className="text-right px-2 text-zinc-100 font-semibold">{m.win_rate_pct != null ? fmt(m.win_rate_pct, 1, '%') : '—'}</td>
                      <td className="text-right px-2 text-zinc-300">{fmt(m.profit_factor, 2)}</td>
                      <td className={`text-right px-2 ${(m.expectancy_pct || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmt(m.expectancy_pct, 3, '%')}</td>
                      <td className="text-right px-2 text-zinc-300">{fmt(m.avg_confidence, 0)}</td>
                      <td className="text-right px-2 text-zinc-300">{fmt(m.avg_score, 1)}</td>
                      <td className="text-right pl-2">
                        {edge
                          ? <Badge data-testid={`sent-edge-badge-${rk}`} className={`${edge.cls} text-white ${edge.strong ? 'font-semibold' : ''}`}>{edge.label}</Badge>
                          : <span className="text-zinc-600 text-xs" data-testid={`sent-edge-none-${rk}`}>acumulando…</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Propuesta de pesos por régimen */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Scale className="w-4 h-4 text-violet-400" /> Propuesta de pesos (data-driven)</CardTitle>
          <CardDescription className="text-xs">Pesos actuales vs recomendados por factor y régimen. Mínimo {minSamples} señales evaluadas por régimen para proponer.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.keys(proposals).length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4" data-testid="sent-no-proposals">Sin propuestas todavía.</p>
          ) : (
            Object.entries(proposals).map(([rk, prop]) => {
              const hasData = prop && prop.weights;
              return (
                <div key={rk} className="border border-zinc-800 rounded-md p-3" data-testid={`sent-proposal-${rk}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">{regimeBadge(rk)}</div>
                    {hasData
                      ? <Badge className="bg-emerald-700 text-white" data-testid={`sent-status-${rk}`}>propuesta disponible</Badge>
                      : <Badge className="bg-zinc-700 text-white" data-testid={`sent-status-${rk}`}>{prop?.status || 'sin datos suficientes'}</Badge>}
                  </div>
                  {hasData && (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                          <th className="text-left py-1">Factor</th>
                          <th className="text-right px-2">Actual</th>
                          <th className="text-right px-2">Recomendado</th>
                          <th className="text-right px-2">Δ</th>
                          <th className="text-right pl-2">Edge medio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {FACTOR_ORDER.map((f) => {
                          const cur = currentW[f];
                          const rec = prop.weights[f];
                          const diff = (rec != null && cur != null) ? rec - cur : null;
                          const edge = prop.edges_avg_pct ? prop.edges_avg_pct[f] : null;
                          return (
                            <tr key={f} className="border-b border-zinc-800/40">
                              <td className="py-1 text-zinc-300">{FACTOR_LABELS[f]} <span className="text-zinc-600 text-xs">({WEIGHT_ENV[f]})</span></td>
                              <td className="text-right px-2 text-zinc-400">{fmt(cur, 1)}</td>
                              <td className="text-right px-2 text-zinc-100 font-semibold">{fmt(rec, 1)}</td>
                              <td className={`text-right px-2 ${diff > 0 ? 'text-emerald-400' : diff < 0 ? 'text-rose-400' : 'text-zinc-500'}`}>
                                {diff == null ? '—' : `${diff > 0 ? '+' : ''}${diff.toFixed(1)}`}
                              </td>
                              <td className={`text-right pl-2 ${(edge || 0) >= 0 ? 'text-emerald-400/80' : 'text-rose-400/80'}`}>{fmt(edge, 4)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
};
